import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

import "./UserRefunds.css";


/* =========================================================
   CONFIG
========================================================= */

const BETS_COLLECTION = "antscore";
const USERNAME_STORAGE_KEY = "username";


/* =========================================================
   GET USERNAME
========================================================= */

const getLocalStorageUsername = () => {
  const directUsername =
    localStorage.getItem(USERNAME_STORAGE_KEY);

  if (
    directUsername &&
    directUsername.trim()
  ) {
    return directUsername.trim();
  }

  const possibleKeys = [
    "user",
    "currentUser",
    "loggedInUser",
    "currentUserData",
  ];

  for (const key of possibleKeys) {
    try {
      const raw = localStorage.getItem(key);

      if (!raw) {
        continue;
      }

      try {
        const parsed = JSON.parse(raw);

        if (
          parsed &&
          typeof parsed === "object" &&
          parsed.username
        ) {
          return String(
            parsed.username
          ).trim();
        }
      } catch {
        if (raw.trim()) {
          return raw.trim();
        }
      }
    } catch (error) {
      console.warn(
        `Unable to read localStorage key "${key}"`,
        error
      );
    }
  }

  return "";
};


/* =========================================================
   DATE
========================================================= */

const getDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (
    typeof value?.toDate === "function"
  ) {
    const date = value.toDate();

    if (
      date instanceof Date &&
      !Number.isNaN(date.getTime())
    ) {
      return date;
    }
  }

  if (
    typeof value?.seconds === "number"
  ) {
    const date = new Date(
      value.seconds * 1000
    );

    if (
      !Number.isNaN(date.getTime())
    ) {
      return date;
    }
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "number") {
    const milliseconds =
      value > 100000000000
        ? value
        : value * 1000;

    const date = new Date(
      milliseconds
    );

    if (
      !Number.isNaN(date.getTime())
    ) {
      return date;
    }
  }

  const parsed = new Date(value);

  if (
    !Number.isNaN(parsed.getTime())
  ) {
    return parsed;
  }

  return null;
};


/* =========================================================
   REFUND DATE
========================================================= */

const getRefundDate = (bet) => {
  return (
    getDateValue(bet.refundedAt) ||
    getDateValue(bet.updatedAt) ||
    getDateValue(bet.createdAt) ||
    getDateValue(bet.timestamp)
  );
};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (value) => {
  const date = getDateValue(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};


/* =========================================================
   SHORT DATE
========================================================= */

const formatShortDate = (value) => {
  const date = getDateValue(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
};


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (
  amount,
  currency
) => {
  const numericAmount =
    Number(amount || 0);

  return `${numericAmount.toLocaleString()} ${
    currency || ""
  }`;
};


/* =========================================================
   COMPONENT
========================================================= */

export default function UserRefunds() {

  const [
    refunds,
    setRefunds,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    username,
    setUsername,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    selectedRefund,
    setSelectedRefund,
  ] = useState(null);


  /* =======================================================
     FETCH REFUNDS
  ======================================================= */

  const fetchRefunds = useCallback(
    async () => {

      setLoading(true);
      setError("");

      try {

        const localUsername =
          getLocalStorageUsername();

        if (!localUsername) {

          setRefunds([]);
          setUsername("");

          setError(
            "Username haijapatikana kwenye localStorage."
          );

          return;
        }

        setUsername(
          localUsername
        );


        const betsRef =
          collection(
            db,
            BETS_COLLECTION
          );


        const q = query(
          betsRef,
          where(
            "username",
            "==",
            localUsername
          )
        );


        const snap =
          await getDocs(q);


        const refundList =
          snap.docs
            .map((document) => ({
              id: document.id,
              ...document.data(),
            }))
            .filter(
              (bet) =>
                bet.refunded === true
            );


        refundList.sort(
          (a, b) => {

            const dateA =
              getRefundDate(a)
                ?.getTime() || 0;

            const dateB =
              getRefundDate(b)
                ?.getTime() || 0;

            return dateB - dateA;
          }
        );


        setRefunds(
          refundList
        );

      } catch (err) {

        console.error(
          "Failed loading refunds:",
          err
        );

        setRefunds([]);

        setError(
          err?.message ||
          "Imeshindikana kupakia refund history."
        );

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    fetchRefunds();

  }, [
    fetchRefunds,
  ]);


  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredRefunds =
    useMemo(() => {

      const term =
        search
          .trim()
          .toLowerCase();

      if (!term) {
        return refunds;
      }

      return refunds.filter(
        (refund) => {

          const values = [
            refund.username,
            refund.teamA,
            refund.teamB,
            refund.currency,
            refund.amount,
            refund.id,
          ];

          return values.some(
            (value) =>
              String(
                value || ""
              )
                .toLowerCase()
                .includes(term)
          );
        }
      );

    }, [
      refunds,
      search,
    ]);


  /* =======================================================
     TOTALS BY CURRENCY
  ======================================================= */

  const totalRefund =
    useMemo(() => {

      const totals = {};

      refunds.forEach(
        (refund) => {

          const currency =
            String(
              refund.currency ||
              "TZS"
            )
              .trim()
              .toUpperCase();

          const amount =
            Number(
              refund.amount || 0
            );

          totals[currency] =
            (
              totals[currency] ||
              0
            ) + amount;
        }
      );

      return totals;

    }, [
      refunds,
    ]);


  /* =======================================================
     CLOSE MODAL
  ======================================================= */

  const closeModal = () => {
    setSelectedRefund(null);
  };


  /* =======================================================
     ESC KEY
  ======================================================= */

  useEffect(() => {

    const handleKeyDown =
      (event) => {

        if (
          event.key === "Escape"
        ) {
          closeModal();
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };

  }, []);


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="refund-page">


      {/* =================================================
          BACKGROUND
      ================================================= */}

      <div className="refund-glow refund-glow-one" />
      <div className="refund-glow refund-glow-two" />


      <div className="refund-container">


        {/* =================================================
            HEADER
        ================================================= */}

        <header className="refund-header">

          <div className="refund-header-left">

            <div className="refund-mini-badge">
              <span>↩️</span>
              TRANSACTION CENTER
            </div>

            <h1>
              Refund{" "}
              <span>History</span>
            </h1>

            <p>
              Angalia fedha zote zilizorejeshwa
              kwenye balance yako.
            </p>

          </div>


          <button
            type="button"
            className="refund-refresh"
            onClick={fetchRefunds}
            disabled={loading}
          >

            <span className="refresh-icon">
              ↻
            </span>

            {loading
              ? "Loading"
              : "Refresh"}

          </button>

        </header>


        {/* =================================================
            ACCOUNT
        ================================================= */}

        {username && (

          <div className="refund-account">

            <div className="account-icon">
              👤
            </div>

            <div className="account-info">

              <span>
                ACCOUNT
              </span>

              <strong>
                {username}
              </strong>

            </div>

            <div className="account-status">

              <i />

              ACTIVE

            </div>

          </div>

        )}


        {/* =================================================
            SUMMARY
        ================================================= */}

        {!loading &&
        refunds.length > 0 && (

          <section className="refund-summary">

            <div className="summary-top">

              <div>

                <span className="summary-label">
                  TOTAL REFUNDED
                </span>

                <h2>
                  {refunds.length}
                  <small>
                    {" "}Transactions
                  </small>
                </h2>

              </div>

              <div className="summary-icon">
                💰
              </div>

            </div>


            <div className="summary-currencies">

              {Object.entries(
                totalRefund
              ).map(
                ([
                  currency,
                  amount,
                ]) => (

                  <div
                    className="summary-currency"
                    key={currency}
                  >

                    <span>
                      {currency}
                    </span>

                    <strong>
                      {amount.toLocaleString()}
                    </strong>

                  </div>

                )
              )}

            </div>

          </section>

        )}


        {/* =================================================
            SEARCH
        ================================================= */}

        {!loading &&
        refunds.length > 0 && (

          <div className="refund-search">

            <span className="search-icon">
              ⌕
            </span>

            <input
              type="text"
              placeholder="Search match, amount, currency..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
            />

            {search && (

              <button
                type="button"
                className="search-clear"
                onClick={() =>
                  setSearch("")
                }
              >
                ×
              </button>

            )}

          </div>

        )}


        {/* =================================================
            ERROR
        ================================================= */}

        {error && (

          <div className="refund-error">

            <span>
              ⚠️
            </span>

            {error}

          </div>

        )}


        {/* =================================================
            LOADING
        ================================================= */}

        {loading && (

          <div className="refund-loading">

            <div className="digital-loader">

              <span />
              <span />
              <span />

            </div>

            <p>
              Inapakia refund history...
            </p>

          </div>

        )}


        {/* =================================================
            EMPTY
        ================================================= */}

        {!loading &&
        !error &&
        filteredRefunds.length === 0 && (

          <div className="refund-empty">

            <div className="empty-icon">
              ↩️
            </div>

            <h3>
              No Refund History
            </h3>

            <p>
              Hakuna refund iliyopatikana
              kwenye account yako.
            </p>

          </div>

        )}


        {/* =================================================
            TRANSACTIONS
        ================================================= */}

        {!loading &&
        filteredRefunds.length > 0 && (

          <section className="refund-section">

            <div className="section-heading">

              <div>

                <span>
                  YOUR ACTIVITY
                </span>

                <h2>
                  Refund Transactions
                </h2>

              </div>

              <div className="live-badge">

                <i />

                SECURE

              </div>

            </div>


            <div className="refund-list">

              {filteredRefunds.map(
                (refund, index) => {

                  const refundDate =
                    getRefundDate(
                      refund
                    );

                  const currency =
                    String(
                      refund.currency ||
                      "TZS"
                    )
                      .trim()
                      .toUpperCase();


                  return (

                    <article
                      className="refund-card"
                      key={refund.id}
                    >

                      {/* CARD LINE */}

                      <div className="card-line" />


                      {/* NUMBER */}

                      <div className="refund-number">

                        {String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        )}

                      </div>


                      {/* ICON */}

                      <div className="refund-icon">

                        <span>
                          ↩️
                        </span>

                      </div>


                      {/* MAIN */}

                      <div className="refund-main">

                        <div className="refund-card-top">

                          <div>

                            <span className="transaction-label">
                              REFUND
                            </span>

                            <h3>
                              {formatMoney(
                                refund.amount,
                                currency
                              )}
                            </h3>

                          </div>

                          <div className="refund-status">

                            <i />

                            COMPLETED

                          </div>

                        </div>


                        {/* MATCH */}

                        <div className="refund-match">

                          <span className="match-icon">
                            ⚽
                          </span>

                          <div>

                            <small>
                              MATCH
                            </small>

                            <strong>

                              {refund.teamA ||
                                "-"}

                              <b>
                                VS
                              </b>

                              {refund.teamB ||
                                "-"}

                            </strong>

                          </div>

                        </div>


                        {/* META */}

                        <div className="refund-meta">

                          <div>

                            <span>
                              CURRENCY
                            </span>

                            <strong>
                              {currency}
                            </strong>

                          </div>

                          <div>

                            <span>
                              DATE
                            </span>

                            <strong>
                              {formatShortDate(
                                refundDate
                              )}
                            </strong>

                          </div>

                          <div>

                            <span>
                              TIME
                            </span>

                            <strong>
                              {
                                refundDate
                                  ? refundDate.toLocaleTimeString(
                                      undefined,
                                      {
                                        hour:
                                          "2-digit",
                                        minute:
                                          "2-digit",
                                      }
                                    )
                                  : "-"
                              }
                            </strong>

                          </div>

                        </div>

                      </div>


                      {/* VIEW */}

                      <button
                        type="button"
                        className="refund-details-btn"
                        onClick={() =>
                          setSelectedRefund(
                            refund
                          )
                        }
                        aria-label="View refund details"
                      >

                        <span>
                          👁️
                        </span>

                        <small>
                          VIEW
                        </small>

                      </button>

                    </article>

                  );
                }
              )}

            </div>

          </section>

        )}

      </div>


      {/* =================================================
          MODAL
      ================================================= */}

      {selectedRefund && (

        <div
          className="refund-modal-overlay"
          onClick={closeModal}
        >

          <div
            className="refund-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <div className="modal-header">

              <div>

                <div className="modal-badge">
                  ↩️ REFUND
                </div>

                <h2>
                  Transaction Details
                </h2>

                <p>
                  Refund successfully completed
                </p>

              </div>

              <button
                type="button"
                className="modal-close"
                onClick={closeModal}
              >
                ×
              </button>

            </div>


            {/* AMOUNT HERO */}

            <div className="modal-amount">

              <span>
                REFUNDED AMOUNT
              </span>

              <strong>
                {formatMoney(
                  selectedRefund.amount,
                  selectedRefund.currency
                )}
              </strong>

              <div className="modal-success">

                <i />

                Successfully Refunded

              </div>

            </div>


            {/* DETAILS */}

            <div className="modal-details">

              <div className="detail">

                <span>
                  Username
                </span>

                <strong>
                  {
                    selectedRefund.username ||
                    username
                  }
                </strong>

              </div>


              <div className="detail">

                <span>
                  Currency
                </span>

                <strong>
                  {
                    selectedRefund.currency ||
                    "TZS"
                  }
                </strong>

              </div>


              <div className="detail">

                <span>
                  Match
                </span>

                <strong>

                  {selectedRefund.teamA ||
                    "-"}

                  <em>
                    VS
                  </em>

                  {selectedRefund.teamB ||
                    "-"}

                </strong>

              </div>


              <div className="detail">

                <span>
                  Refund Date
                </span>

                <strong>
                  {formatDate(
                    selectedRefund.refundedAt
                  )}
                </strong>

              </div>


              <div className="detail">

                <span>
                  Transaction ID
                </span>

                <strong className="transaction-id">
                  {
                    selectedRefund.id
                  }
                </strong>

              </div>


              <div className="detail">

                <span>
                  Status
                </span>

                <strong className="detail-success">

                  ✓ REFUNDED

                </strong>

              </div>

            </div>


            {/* FOOTER */}

            <div className="modal-footer">

              <button
                type="button"
                onClick={closeModal}
              >
                Close
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

